<template>
	<div class="p-4 bg-white border-t-4 border-indigo-600 shadow">
		<div @click="showAddAttachment = ! showAddAttachment" class="flex items-center justify-between cursor-pointer">
			<div class="flex">
    			<inline-svg name="attachment" classes="w-5 h-5 stroke-gray-500"></inline-svg>
    			<h3 class="ml-2 font-medium all-small-caps">Add attachment</h3>
    		</div>
    		<div class="flex">
				<span
					:class="{ 'rotate-0 ease-out': ! showAddAttachment, 'rotate-90 ease-in': showAddAttachment }"
					class="transform transition-transform duration-100"
				>
					<inline-svg name="chevron-right" classes="w-6 h-6 text-gray-500 fill-current"></inline-svg>
				</span>
			</div>
		</div>

		<transition name="slide">
			<div v-show="showAddAttachment">
				<div v-show="allowUpload" class="mt-5 flex justify-center">
		            <button
		                @click="activeAttachmentTab = 'existing'"
		                type="button"
		                class="px-5 py-3 font-medium leading-5 rounded-md text-gray-500 hover:text-gray-700 focus:outline-none"
		                :class="{ 'text-indigo-600 bg-indigo-100': activeAttachmentTab == 'existing' }"
		            >
		                Existing Document
		            </button>

		            <button
		                @click="activeAttachmentTab = 'new'"
		                type="button"
		                class="px-5 py-3 font-medium leading-5 rounded-md text-gray-500 hover:text-gray-700 focus:outline-none"
		                :class="{ 'text-indigo-600 bg-indigo-100': activeAttachmentTab == 'new' }"
		            >
		                Upload New Document
		            </button>
				</div>

				<div v-if="activeAttachmentTab == 'existing'">
		    		<div class="mt-6 flex space-x-3">
		        		<div class="flex flex-col">
			        		<label for="category" class="text-xs uppercase font-semibold">Category</label>
			        		<select v-model="selectedCategory" name="category" class="mt-2 form-select" @change="attachment_id = ''">
			        			<option value="">Select Category</option>
			        			<option v-for="category in documents.categories" :key="category.id" :value="category.id">{{ category.name }}</option>
			        		</select>
			        	</div>
		        		<div class="flex flex-col">
			        		<label for="subcategory" class="text-xs uppercase font-semibold">Subcategory</label>
			        		<select v-model="selectedSubcategory" name="category" class="mt-2 form-select" @change="attachment_id = ''">
			        			<option value="">Select Subcategory</option>
			        			<option v-for="category in availableSubcategories" :key="category.id" :value="category.id">{{ category.name }}</option>
			        		</select>
			        	</div>
		        	</div>

		    		<div class="mt-6 flex flex-col" v-if="filteredDocuments.length" name="document">
						<label for="document" class="text-xs uppercase font-semibold">Document</label>
		        		<select v-model="attachment_id" class="mt-2 form-select">
		        			<option value="">Select Document</option>
		        			<option v-for="file in filteredDocuments" :key="file.id" :value="file.id">{{ file.name }}</option>
		        		</select>
		        	</div>
		        	<div v-if="selectedCategory != '' && ! filteredDocuments.length" class="mt-6">
		        		<div class="text-sm text-center italic">No Documents Found</div>
		        	</div>
		        </div>

		        <div v-show="allowUpload && activeAttachmentTab == 'new'" class="mt-3">
			        <form
			            action="/documents"
			            method="POST"
			            class="mt-6 dropzone"
			            :id="uniqueId"
			        >
			            <div class="dz-message flex flex-col items-center" data-dz-message>
			                <inline-svg name="upload" classes="w-8 h-8 text-gray-400 fill-current"></inline-svg>
			                <span class="mt-4 font-thin">Click here to upload</span>
			            </div>
			        </form>
		        </div>
			</div>
		</transition>

        <dropzone-template @dropzone-template-initialized="setPreviewTemplate"></dropzone-template>
	</div>
</template>

<script>
	import { mapState } from 'vuex';
	import Dropzone from 'dropzone';

	export default {
		props: {
            commentableData: {
                type: [Array, Object],
                required: true
            },

            allowUpload: {
            	type: Boolean,
            	default: false
            }
		},

		data() {
			return {
				attachment_id: '',
        		selectedCategory: '',
        		selectedSubcategory: '',
        		showAddAttachment: false,
        		activeAttachmentTab: 'existing'
			}
		},

        mounted() {
        	this.$store.dispatch('loadCategories');

        	this.initDropzone();

        	this.$eventHub.$on('upload-comment-attachment', () => {
				this.dropzone.processQueue();
        	});
        },

        watch: {
        	attachment_id() {
        		this.$emit('document-attached', this.attachment_id);
        	}
        },

		computed: {
			...mapState(['documents']),

			filteredDocuments() {
				return this.documents.all.filter(file => {
					if (this.selectedSubcategory != '') {
						return file.category_id == this.selectedCategory && file.subcategory_id == this.selectedSubcategory
					} else {
						return file.category_id == this.selectedCategory;
					}
				});
			},

			uniqueId() {
				return 'dropzone-' + Math.random().toString(36).substring(7);
			},

            availableSubcategories() {
                if (this.selectedCategory == '') {
                    return '';
                }

                return _.filter(this.documents.subcategories, (item) => {
                    return item.category_id == this.selectedCategory
                })
            }
		},

		methods: {
            initDropzone() {
                Dropzone.autoDiscover = false;

                this.dropzone = new Dropzone("#" + this.uniqueId, {
                	acceptedFiles: "image/*,.pdf,.mov,.avi,.mp3,.mp4,.mpeg,.wav,.rtf,.txt,.numbers,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odp,.ods,.odt,.oga,.ogv",
                    maxFiles: 1,
                    maxFilesize: 150, // MB
                    uploadMultiple: false,
                    autoProcessQueue: false,
                    headers: { "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]').getAttribute('content') },
                    addedfiles: files => { // attempt to automatically set the document category for the user
                        files.forEach((file, index) => {
                            var categorySelect = document.querySelectorAll(".file-category")[index];
                            var subCategorySelect = document.querySelectorAll(".file-subcategory")[index];
                            var description = document.querySelectorAll(".file-description")[index];
                            
                            categorySelect.addEventListener('change', (select) => {
                                let subcategories = _.filter(this.documents.subcategories, ['category_id', parseInt(select.target.value)]);
                                
                                this.clearSelect(subCategorySelect);

                                subcategories.forEach(subcategory => {
                                    var option = document.createElement("option");
                                    option.value = subcategory.id;
                                    option.text = subcategory.name;
                                    subCategorySelect.add(option);
                                });
                            })

                            // scan file name for text that matches our document categories
                            // if found set the category automatically for the user
                            this.documents.categories.forEach(category => {
                                if (file['name'].split(/(?=[A-Z])|[\s_,.-]+/g).some(word => category.name.toLowerCase().split(' ').indexOf(word.toLowerCase()) >= 0)) {
                                    var option = this.findOption(categorySelect, category.name);
                                    categorySelect.value = option.value;
                                    categorySelect.dispatchEvent(new Event("change"));
                                }
                            });

                            // if no category was found in the file name then if
                            // the file is an image set the category to Photo     
                            if (! categorySelect.value && file['type'].includes('image')) {
                                var option = this.findOption(categorySelect, 'Photo');
                                categorySelect.value = option.value;
                                categorySelect.dispatchEvent(new Event("change"));
                            }

                            file.categorySelect = categorySelect;
                            file.subCategorySelect = subCategorySelect;
                            file.descriptionInput = description;
                        })
                    },
                    sending: (file, xhr, formData) => {
                        formData.set('client_id', this.commentableData.client_id);
                        formData.set('documentable_id', this.commentableData.id);
                        formData.set('documentable_type', this.commentableData.class_name);
                        formData.set('category_id', file.categorySelect?.value || '');
                        formData.set('subcategory_id', file.subCategorySelect?.value || '');
                        formData.set('description', file.descriptionInput?.value || '');
                    },
                    complete: (file) => {
                    	console.log(JSON.parse(file.xhr.response));

                    	this.attachment_id = JSON.parse(file.xhr.response).documents[0].id;

                    	this.dropzone.removeAllFiles();

                    	document.querySelector('#' + this.uniqueId + ' .dz-message').classList.remove('hidden');
                    }
                });
            },

            findOption(select, text) {
                return Array.from(select.querySelectorAll('option')).find(el => RegExp(text).test(el.textContent));
            },

            clearSelect(element) {                
                for(var i = element.options.length; i >= 0; i--) {
                    element.remove(i);
                }

                var option = document.createElement("option");
                option.value = '';
                option.text = 'Select Subcategory';
                element.add(option);
            },

            setPreviewTemplate() {
                this.dropzone.options.previewTemplate = document.querySelector('#dropzone-template').innerHTML;
            }
		}
	}
</script>
